import { IsNotEmpty, IsString } from 'class-validator';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import createCollectionResource, { UpdateOptions } from 'src/actions/shared/createResource';
import { DomainError as StandardDomainError } from '@ad-scripts/application/lib/error';
import { IsPropertyUniqueInStoreSlice } from 'src/actions/validators/IsPropertyUniqueInStoreSlice';

export type Permission =
  | 'agent'
  | 'analyst'
  | 'answers:read'
  | 'answers:admin'
  | 'api-user'
  | 'compliance-admin'
  | 'conversation:callCoach'
  | 'conversation:callRecordingListen'
  | 'conversation:general'
  | 'conversation:outbound'
  | 'conversation:read'
  | 'conversation:socialCommentModeration'
  | 'customer:dedicatedHero'
  | 'developer'
  | 'inboxes:viewAll'
  | 'liveboards'
  | 'login'
  | 'reports:read'
  | 'system-admin'
  | 'task-user'
  | 'team-manager'
  | 'topics:admin';

export class Role {
  id: string;
  disabledAt?: string;
  builtin: boolean;

  @IsString()
  @IsNotEmpty({ message: 'Please enter the role name' })
  @IsPropertyUniqueInStoreSlice('roles', { message: 'This name already exists. Please create a new one.' })
  name: string;

  description?: string;
  permissions: Permission[];

  version?: number;
}

const rolesResource = createCollectionResource<Role>({
  name: 'roles',
  type: Role,
  endpoint: {
    fetchAllUrl: '/api/v1/orgs/:orgId/roles',
    replaceUrl: '/api/v1/orgs/:orgId/roles',
    broadcastTopic: 'v1/orgs/:orgId/roles/:id',
    collectionBroadcastTopic: 'v1/orgs/:orgId/roles/+',
  },
  observerCreator: ({ actions }) => {
    return ({ dispatch }) => {
      return {
        onBroadcast(item) {
          dispatch(actions.broadcast(item));
        },
      };
    };
  },
  options: {
    toastMessages: {
      onUpdate: (role: Role) => `"${role.name}" has been saved.`,
      onUpdateFail: (role: Role, err: unknown) => {
        if (err instanceof StandardDomainError && err.errors[0].code === 'in_use') {
          return `${role.name} can't be archived because it's assigned to a user.`;
        }

        return `Unable to save "${role.name}". Please try again.`;
      },
    },
  },
});

const { update } = rolesResource.effects;

/**
 * Calls update effect to add/update a role.
 * @param {Partial<Role>} params - Parameters for the request.
 * @param {Role} attrs - The role to be added/updated.
 * @param {UpdateOptions} opts - Optional replace options
 * @returns A thunk function that performs the replace operation.
 */
const replace: sharedTypes.AppThunk = (params: Partial<Role>, attrs: Role, opts?: UpdateOptions) => async dispatch => {
  if (attrs.version) {
    delete attrs.version;
  }
  // Add login permission by default
  if (attrs.permissions) {
    if (attrs.permissions.length === 1 && attrs.permissions.includes('login')) {
      attrs.permissions.splice(0);
    } else if (attrs.permissions.length > 0 && !attrs.permissions.includes('login')) {
      attrs.permissions.push('login');
    }
  }
  dispatch(update(params, attrs, opts));
};

rolesResource.effects.replace = replace;

export default rolesResource;
